import type { ReactNode } from 'react';
import { twMerge } from 'cn';
import { Spinner } from '@studio/design-system/spinner';
import { Tooltip } from '@studio/design-system/tooltip';
import { PRESET_CHECKING, PRESET_UNSUPPORTED } from '#~/control/preset/access.js';
import type { PresetAccess } from '#~/control/preset/access.js';

// Per rule: absent-data-gates-permissively (ai.knowledge/global) — unknown/error
// render the controls as usable; only a loaded 'unsupported' disables them.
export const Availability = ({
  access,
  checking = false,
  children,
  className
}: {
  access: PresetAccess;
  /**
   * An operator's action is waiting for the camera's capabilities
   * (awaitPresetAllowed). Unknown access alone shows nothing.
   */
  checking?: boolean;
  children: ReactNode;
  /**
   * Classes applied to every wrapper box the gate inserts. A caller inside a
   * flex row passes flex-item classes so the wrappers don't break its layout.
   */
  className?: string;
}) => {
  const disabled = access === 'unsupported';
  // Keep the controls visible; inert blocks menus/dragging as well as buttons.
  const content = (
    <div
      className={twMerge(disabled && 'opacity-50', className)}
      ref={(node) => {
        node?.toggleAttribute('inert', disabled);
      }}
    >
      {children}
    </div>
  );
  return (
    <div className={className}>
      {checking && (
        <div role='status' className='flex items-center gap-8 p-8'>
          <Spinner />
          {PRESET_CHECKING}
        </div>
      )}
      {disabled ? (
        <Tooltip
          trigger={
            <div
              className={className}
              tabIndex={0}
              role='group'
              aria-label={PRESET_UNSUPPORTED}
              aria-disabled='true'
            >
              {content}
            </div>
          }
        >
          {PRESET_UNSUPPORTED}
        </Tooltip>
      ) : (
        content
      )}
    </div>
  );
};
